import React, { useEffect, useCallback, createContext, useState, useContext, Dispatch, SetStateAction, useMemo } from 'react'
import type Stripe from 'stripe'
import { UserType } from '../../server/models/User'
import { getSession, logout } from '../requests/session'
import { useActiveSubscriptionQuery } from '../requests/billing'

export type AuthContextType = {
  authUser: UserType | null
  setAuthUser: Dispatch<SetStateAction<UserType | null>>

  loading: boolean
  checkAuth: () => Promise<void>

  startLogout: () => Promise<void>

  subscription: Stripe.Subscription | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

type Props = {
    children: React.ReactNode
}

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [authUser, setAuthUser] = useState<UserType | null>(null)
  const [loading, setLoading] = useState(true)

  // Fetch active subscription when authUser is available or changes
  const { data: subscriptionData } = useActiveSubscriptionQuery(authUser?._id)

  // Extract subscription from query data
  const subscription = useMemo(() => {
    return subscriptionData?.subscription ?? null
  }, [subscriptionData])

  const checkAuth = useCallback(async () => {
    setLoading(true)

    try {
      const res = await getSession()
      setAuthUser(res.data)
    } catch (_err) {
      setAuthUser(null)
    }

    setLoading(false)
  }, [])

  useEffect(() => {
    checkAuth()
    window.addEventListener('focus', checkAuth)

    return () => {
      window.removeEventListener('focus', checkAuth)
    }
  }, [checkAuth])

  const startLogout = useCallback(async () => {
    await logout()
    localStorage.clear()
    window.location.href = '/'
  }, [])

  return (
    <AuthContext.Provider value={{ authUser, setAuthUser, loading, checkAuth, startLogout, subscription }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
